import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Reveal } from "../components/Reveal";
import { useCart } from "../context/CartContext";
import { useLang } from "../context/LanguageContext";
import { useImages } from "../context/ContentContext";
import { api } from "../lib/api";
import { toast } from "sonner";

const ORDER = { femme: 0, mixte: 1, homme: 2 };

export default function Coffrets() {
  const { add, items } = useCart();
  const { t } = useLang();
  const IMG = useImages();
  const nav = useNavigate();
  const [coffrets, setCoffrets] = useState([]);
  const [threshold, setThreshold] = useState(20);

  useEffect(() => {
    api.get("/products?category=coffret")
      .then((r) => {
        const sorted = [...r.data].sort((a, b) => (ORDER[a.universe] ?? 9) - (ORDER[b.universe] ?? 9));
        setCoffrets(sorted);
      })
      .catch(() => setCoffrets([]));
    api.get("/settings").then((r) => setThreshold(r.data.low_stock_threshold ?? 20)).catch(() => {});
  }, []);

  const coffretImage = (c) => {
    const byUniverse = {
      femme: IMG.coffretFemme,
      mixte: IMG.coffretMixte,
      homme: IMG.coffretHomme,
    };
    return byUniverse[c.universe] || c.image || IMG.coffrets;
  };

  const addBox = (c) => {
    // Les paniers mixtes Femme + Homme sont autorisés.
    add({ key: c.lookup_key, label: `${c.name} — ${t("coffrets.edition")}`, unit: c.price, image: coffretImage(c), universe: c.universe }, 1);
    toast.success(`${c.name} ${t("coffrets.added")}`);
    nav("/panier");
  };

  return (
    <div className="pt-32 pb-28">
      <div className="max-w-4xl mx-auto px-6 text-center mb-4">
        <Reveal>
          <p className="overline mb-4">{t("coffrets.overline")}</p>
          <h1 className="font-display text-5xl md:text-7xl mb-6" style={{ fontWeight: 300 }}>{t("coffrets.title_pre")}<span className="rose-gold-text italic">Luxe Oryvea</span></h1>
          <p className="text-sm max-w-xl mx-auto" style={{ color: "var(--lo-muted)" }}>{t("coffrets.intro")}</p>
        </Reveal>
      </div>
      <Reveal>
        <div className="max-w-6xl mx-auto px-6 md:px-12 mb-16">
          <img src={IMG.coffrets} alt="Coffrets LUXE ORYVEA PREMIUM" loading="lazy" decoding="async" className="w-full h-[300px] md:h-[440px] object-cover" style={{ border: "1px solid var(--lo-border-rose)" }} />
        </div>
      </Reveal>
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-3 gap-6">
        {coffrets.map((c, i) => {
          const out = (c.stock ?? 0) <= 0;
          const low = !out && (c.stock ?? 0) <= threshold;
          return (
          <Reveal key={c.id} delay={i * 0.1}>
            <div className="card-lux overflow-hidden h-full flex flex-col" data-testid={`coffret-${c.universe}`}>
              <div className="relative">
                <img src={coffretImage(c)} alt={c.name} loading="lazy" decoding="async" className="w-full h-64 object-cover" />
                {c.badge && <span className="absolute top-3 right-3 badge-save text-[9px] px-3 py-1 uppercase">{c.badge}</span>}
                {out ? (
                  <span className="absolute top-3 left-3 text-[9px] px-3 py-1 uppercase tracking-wider" style={{ background: "#3a2a2a", color: "#e8b4b4" }} data-testid={`coffret-out-${c.universe}`}>{t("coffrets.out")}</span>
                ) : low ? (
                  <span className="absolute top-3 left-3 text-[9px] px-3 py-1 uppercase tracking-wider font-semibold" style={{ background: "var(--lo-rose-hi)", color: "#0A0505" }} data-testid={`coffret-lowstock-${c.universe}`}>{t("coffrets.lowstock")}</span>
                ) : null}
              </div>
              <div className="p-7 flex flex-col flex-1">
                <h2 className="font-display text-3xl mb-3" style={{ fontWeight: 400 }}>{c.name}</h2>
                <p className="text-sm leading-relaxed mb-5 flex-1" style={{ color: "var(--lo-muted)" }}>{c.description}</p>
                {low && <p className="text-xs mb-3" style={{ color: "var(--lo-rose-hi)" }}>{t("coffrets.only_left_pre")}{c.stock}{t("coffrets.only_left_suf")}</p>}
                <div className="flex items-center justify-between">
                  <span className="font-display text-3xl rose-gold-text">{Number(c.price).toFixed(2).replace(".", ",")} €</span>
                  <button className="btn-primary h-12 px-6 disabled:opacity-40 disabled:cursor-not-allowed" disabled={out} onClick={() => addBox(c)} data-testid={`coffret-add-${c.universe}`}>{out ? t("coffrets.out") : t("coffrets.add")}</button>
                </div>
              </div>
            </div>
          </Reveal>
          );
        })}
      </div>
    </div>
  );
}
